import { useState, useMemo } from "react";
import { useSeo } from "@/lib/seo";
import { ARTICLES, CATEGORIES } from "@/data/articles";
import { PageHeader } from "@/components/site/PageHeader";
import { ArticleCard } from "@/components/site/ArticleCard";
import { ContextualCTA } from "@/components/site/ContextualCTA";
import { cn } from "@/lib/utils";

export default function Journal() {
  const [active, setActive] = useState("All");
  useSeo({
    title: "Journal — Intelligence Library",
    description:
      "The Selling Sunsets Intelligence Library — substantive, source-led research on buying property in Uttarakhand: land law, due diligence, circle rates, RERA, second homes and more.",
    path: "/journal",
  });

  const filtered = useMemo(
    () => (active === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === active)),
    [active]
  );
  const [feature, ...rest] = filtered;
  const chips = ["All", ...CATEGORIES.filter((c) => ARTICLES.some((a) => a.category === c))];

  return (
    <>
      <PageHeader
        eyebrow="Intelligence Library"
        marker="SS·JNL"
        title="The Journal"
        intro="Research that answers real buyer questions. Every piece separates what is reliably true from what must be checked — and points to primary sources where a claim depends on them."
        breadcrumbs={[{ label: "Journal" }]}
      />

      <section className="ss-container pb-10">
        <div className="flex flex-wrap gap-2.5 border-y border-[#E2DDD5] py-5" data-testid="journal-filters">
          {chips.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`journal-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={cn(
                "px-4 py-2 text-[0.72rem] uppercase tracking-[0.08em] border transition-colors",
                active === c
                  ? "bg-[#1A1C1E] text-[#F7F5F0] border-[#1A1C1E]"
                  : "bg-transparent text-[#52575D] border-[#C8C2B7] hover:border-[#1A1C1E] hover:text-[#1A1C1E]"
              )}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {feature && (
        <section className="ss-container pb-16">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
            <ArticleCard article={feature} index={0} feature />
            <div className="hidden lg:block">
              <p className="ss-overline text-[#8C6A53] mb-3">Featured</p>
              <h2 className="font-display text-3xl font-light leading-snug text-[#1A1C1E]">{feature.summary}</h2>
            </div>
          </div>
        </section>
      )}

      <section className="ss-container pb-8">
        <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
          {rest.map((a, i) => <ArticleCard key={a.slug} article={a} index={i} />)}
        </div>
        {filtered.length === 0 && (
          <p className="text-[#52575D] font-light py-12">No research in this category yet.</p>
        )}
      </section>

      <ContextualCTA
        title="Researching a real decision?"
        text="The Journal is a starting point. For your specific situation, request an independent buyer-side assessment."
        whatsappMessage="Hi Suhail, I've been reading the Selling Sunsets Journal and would like to discuss my situation."
      />
    </>
  );
}
